import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  keyframes,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaArrowLeft,
  FaDownload,
  FaEnvelope,
} from "react-icons/fa";
import { CheckCircle, Sparkles, Download, ArrowLeft, Mail } from "lucide-react";

interface ThankYouProps {
  onBack: () => void;
  proposal: any;
}

const scaleIn = keyframes`
  0% { transform: scale(0) rotate(-180deg); opacity: 0; }
  50% { transform: scale(1.1) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

const ThankYou: React.FC<ThankYouProps> = ({ onBack, proposal }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowContent(true), 300);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, blue.50, purple.50, pink.50)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      p={4}
    >
      {/* Background Decorative Elements */}
      <Box
        position="absolute"
        top="10%"
        left="10%"
        w="300px"
        h="300px"
        borderRadius="full"
        bgGradient="linear(to-br, blue.200, purple.200)"
        opacity={0.2}
        filter="blur(80px)"
        animation={`${float} 6s ease-in-out infinite`}
      />
      <Box
        position="absolute"
        bottom="10%"
        right="10%"
        w="250px"
        h="250px"
        borderRadius="full"
        bgGradient="linear(to-br, purple.200, pink.200)"
        opacity={0.2}
        filter="blur(80px)"
        animation={`${float} 8s ease-in-out infinite`}
      />

      {/* Sparkle Elements */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={i}
          position="absolute"
          top={`${Math.random() * 100}%`}
          left={`${Math.random() * 100}%`}
          animation={`${sparkle} ${
            2 + Math.random() * 2
          }s ease-in-out infinite`}
        >
          <Sparkles
            size={16 + Math.random() * 16}
            color={
              ["#3B82F6", "#9333EA", "#EC4899"][Math.floor(Math.random() * 3)]
            }
          />
        </Box>
      ))}

      {/* Main Content */}
      <VStack
        spacing={8}
        bg="white"
        borderRadius="3xl"
        boxShadow="2xl"
        p={{ base: 8, md: 12 }}
        maxW="600px"
        w="full"
        position="relative"
        border="2px solid"
        borderColor="green.100"
        animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
      >
        {/* Success Icon */}
        <Box position="relative">
          <Box animation={`${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)`}>
            <Box
              bgGradient="linear(to-br, green.400, green.600)"
              borderRadius="full"
              p={8}
              boxShadow="0 20px 40px -10px rgba(34, 197, 94, 0.4)"
              position="relative"
            >
              <Icon as={FaCheckCircle} w={20} h={20} color="white" />

              {/* Pulsing Ring */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="full"
                h="full"
                borderRadius="full"
                border="4px solid"
                borderColor="green.300"
                animation={`${float} 2s ease-in-out infinite`}
                opacity={0.6}
              />
            </Box>
          </Box>
        </Box>

        {/* Thank You Text */}
        <VStack spacing={3}>
          <Text
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="900"
            bgGradient="linear(to-r, green.600, green.700)"
            bgClip="text"
            textAlign="center"
            animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
          >
            Thank You!
          </Text>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="700"
            color="gray.800"
            textAlign="center"
            animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
          >
            Proposal Accepted Successfully
          </Text>
        </VStack>

        {/* Success Message */}
        <Box
          bg="green.50"
          border="2px solid"
          borderColor="green.200"
          borderRadius="2xl"
          p={6}
          w="full"
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          <VStack spacing={3} align="start">
            <HStack spacing={2}>
              <Icon as={CheckCircle} w={5} h={5} color="green.600" />
              <Text fontSize="sm" fontWeight="700" color="green.900">
                What happens next?
              </Text>
            </HStack>
            <VStack align="start" spacing={2} pl={7}>
              <Text fontSize="sm" color="green.800" lineHeight="1.6">
                • Your signature has been securely recorded
              </Text>
              <Text fontSize="sm" color="green.800" lineHeight="1.6">
                • The proposal owner will be notified immediately
              </Text>
              <Text fontSize="sm" color="green.800" lineHeight="1.6">
                • You'll receive a confirmation email shortly
              </Text>
              <Text fontSize="sm" color="green.800" lineHeight="1.6">
                • Our team will contact you within 24-48 hours
              </Text>
            </VStack>
          </VStack>
        </Box>

        {/* Action Buttons */}
        <VStack
          w="full"
          spacing={3}
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          <Button
            leftIcon={<ArrowLeft size={18} />}
            size="lg"
            w="full"
            fontSize="sm"
            fontWeight="700"
            variant="outline"
            borderWidth="2px"
            borderColor="gray.300"
            color="gray.700"
            _hover={{
              borderColor: "gray.400",
              bg: "gray.50",
              transform: "translateY(-2px)",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            transition="all 0.2s"
            onClick={handleRefresh}
          >
            View Proposal Again
          </Button>
        </VStack>

        {/* Footer Note */}
        <Text
          fontSize="xs"
          color="gray.500"
          textAlign="center"
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          Need help? Contact us at {proposal?.companyInfo?.Email}
        </Text>
      </VStack>
    </Box>
  );
};

export default ThankYou;
